/**
 * The Operate gadgets' plain logic (components/home/gadgets: Uptime and resources, Jobs, Data, Cost, Backups). Pure:
 * the services' state, the app configuration, the data overview, the person's credits and the restore points go in.
 * The cost in production is an estimate (vfs-service's FinOps model, with the admin's defaults) and is called one.
 */
import { serviceState } from '../../services/SandboxService';
import { shortServiceName } from '../gitRepoSource';
import { STORE_KINDS, storeLabel } from '../dataModel';

const num = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

/**
 * Per service: its state, since when it is up (`startedAt`, only while it runs), its restarts when the runner counts
 * them, CPU (percent of one core) and memory (bytes) now. `[{ service, state, since, restarts, cpu, memory }]`.
 */
export const uptimeRows = (items = []) => items.map(({ serviceName, service }) => {
  const state = serviceState(service);
  const up = state === 'running';
  return {
    service: shortServiceName(serviceName),
    state,
    since: up ? service?.startedAt || service?.createdAt || null : null,
    restarts: Number.isFinite(service?.restartCount) ? service.restartCount : null,
    cpu: up ? num(service?.resources?.cpuPercent) : null,
    memory: up ? num(service?.resources?.memoryBytes) : null,
  };
});

/**
 * Every scheduled job of the services read (`details`: service → its configuration with `jobs`), soonest next run
 * first, the paused ones last: `[{ service, name, describe, nextRunAt, lastRun, enabled, running }]`.
 */
export function jobRows(details = {}) {
  const rows = Object.entries(details || {}).flatMap(([service, d]) => (d?.jobs || []).map((job) => ({
    service,
    name: job.name,
    describe: job.describe || job.schedule || '',
    nextRunAt: job.enabled === false ? null : job.nextRunAt || null,
    lastRun: job.lastRun || null,
    enabled: job.enabled !== false,
    running: Boolean(job.running),
  })));
  const at = (row) => (row.nextRunAt ? Date.parse(row.nextRunAt) : Infinity);
  return rows.sort((a, b) => Number(b.enabled) - Number(a.enabled) || at(a) - at(b) || a.name.localeCompare(b.name));
}

/** How many jobs failed (or timed out) the last time they ran. */
export const failedJobs = (rows = []) => rows.filter((r) => r.lastRun && (r.lastRun.status === 'failed' || r.lastRun.status === 'timeout')).length;

/**
 * The datastores (`GET /data` stores): `[{ id, label, status, objects, noun, records }]`, `records` the sum of the
 * counts the store reports (null when it reports none: S3 counts files, Kafka nothing).
 */
export const dataRows = (overview) => (overview?.stores || []).map((store) => {
  const objects = store.objects || [];
  const counted = objects.map((o) => num(o.count)).filter((n) => n !== null);
  const noun = STORE_KINDS[store.kind]?.noun || ['object', 'objects'];
  return {
    id: store.id || store.kind,
    label: storeLabel(store),
    status: store.status || null,
    objects: objects.length,
    noun: objects.length === 1 ? noun[0] : noun[1],
    records: store.kind === 's3' || store.kind === 'kafka' || counted.length === 0 ? null : counted.reduce((a, b) => a + b, 0),
  };
});

/**
 * Cost: the person's credits spent this month (all their projects: the gateway keeps them per person) and the app's
 * production estimate per month with the FinOps defaults the Flow admin set (runtime, region, scenario; no AI calls),
 * as vfs-service's FinOps model answers it (`finops`: GET /finops with no choices, services/FinOpsService.js) from what
 * each service uses now and the project's datastores. `{ spent, estimate, measured, services, runtime, region,
 * requestsPerDay, symbol }`; `spent` null without billing, `estimate` null without a service (or an answer).
 */
export function costSummary({ me = null, finops = null } = {}) {
  const spentRaw = me?.spentThisMonth?.credits;
  const spent = typeof spentRaw === 'number' && Number.isFinite(spentRaw) ? spentRaw : null;
  const services = finops?.counts?.services || 0;
  const runtime = finops ? (finops.choices?.runtimes || []).find((r) => r.id === finops.params?.runtime)?.label || finops.params?.runtime || null : null;
  return {
    spent,
    estimate: services > 0 && finops?.production ? finops.production.total : null,
    measured: finops?.counts?.measured || 0,
    services,
    runtime,
    region: finops?.params?.region || null,
    requestsPerDay: finops?.params?.traffic?.requestsPerDay ?? null,
    symbol: finops?.currency?.symbol || '€',
  };
}

/**
 * Backups: the restore points (newest first) and the sandbox's nightly backups. `{ available, reason, last, points,
 * nightly: { available, last, count } }`; `last` the newest restore point.
 */
export function backupSummary({ points = null, backups = null } = {}) {
  const list = Array.isArray(points?.points) ? [...points.points].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))) : [];
  const nightly = Array.isArray(backups?.items) ? [...backups.items].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))) : [];
  return {
    available: points ? points.available !== false : null,
    reason: points?.note || null,
    last: list[0] || null,
    points: list,
    nightly: { available: backups ? backups.available !== false : null, last: nightly[0] || null, count: nightly.length },
  };
}
